import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowLeft } from "lucide-react";
import { FONT_FAMILY } from "../common/sharedFunctions";
import PageSEO from '../components/SEO/PageSEO';

export default function ContactUs() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#009689]/5 via-white to-[#319F44]/5" style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <PageSEO
        title="Contact Us"
        description="Get in touch with Gruwcer Delivery. Contact our support team for delivery inquiries, service questions, or partnership opportunities."
        keywords="contact Gruwcer Delivery, delivery support, delivery help, customer service, delivery booking help"
        path="/contact-us"
      />
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <img src="/gruwcer-logo.png" alt="Gruwcer Delivery" onClick={() => navigate('/')} className="h-8 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: FONT_FAMILY }}
          >
            {t('contact_us')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            style={{ fontFamily: FONT_FAMILY }}
          >
            Have questions or need support? We'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {settings?.contact_email && (
            <motion.a
              href={`mailto:${settings.contact_email}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#009689]/30 transition-all text-center group"
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-[#009689]/10 flex items-center justify-center group-hover:bg-[#009689]/20 transition-colors">
                <Mail className="w-6 h-6 text-[#009689]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: FONT_FAMILY }}>{t('email')}</h3>
              <p className="text-sm text-[#009689]" style={{ fontFamily: FONT_FAMILY }}>{settings.contact_email}</p>
            </motion.a>
          )}

          {settings?.CompanyPhone && (
            <motion.a
              href={`tel:${settings.CompanyPhone}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#009689]/30 transition-all text-center group"
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-[#319F44]/10 flex items-center justify-center group-hover:bg-[#319F44]/20 transition-colors">
                <Phone className="w-6 h-6 text-[#319F44]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: FONT_FAMILY }}>{t('phone')}</h3>
              <p className="text-sm text-[#319F44]" style={{ fontFamily: FONT_FAMILY }}>{settings.CompanyPhone}</p>
            </motion.a>
          )}

          {settings?.CompanyAddress && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#009689]/30 transition-all text-center group"
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: FONT_FAMILY }}>Address</h3>
              <p className="text-sm text-gray-600" style={{ fontFamily: FONT_FAMILY }}>{settings.CompanyAddress}</p>
            </motion.div>
          )}
        </div>

        {/* Company Info & Social */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="max-w-4xl mx-auto mt-12 bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: FONT_FAMILY }}>
            {settings?.CompanyName || 'Gruwcer Delivery'}
          </h3>
          <p className="text-gray-600 mb-6" style={{ fontFamily: FONT_FAMILY }}>
            {t('about_us_content2')}
          </p>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-4">
            {settings?.FacebookHandle && (
              <a href={settings.FacebookHandle} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors">
                <i className="fab fa-facebook text-blue-600" />
              </a>
            )}
            {settings?.TwitterHandle && (
              <a href={settings.TwitterHandle} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-sky-100 hover:bg-sky-200 flex items-center justify-center transition-colors">
                <i className="fab fa-twitter text-sky-600" />
              </a>
            )}
            {settings?.InstagramHandle && (
              <a href={settings.InstagramHandle} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#319F44]/10 hover:bg-[#319F44]/20 flex items-center justify-center transition-colors">
                <i className="fab fa-instagram text-[#319F44]" />
              </a>
            )}
          </div>
        </motion.div>
      </section>
    </div>
  );
}
